import React from "react";
import { MarketingShell } from "@/components/marketing/MarketingShell";

const EFFECTIVE_DATE = "August 14, 2026";
const LAST_UPDATED = "August 14, 2026";

export default function PrivacyPage() {
  return (
    <MarketingShell>
      <section className="max-w-3xl mx-auto px-6 py-16 prose-legal" data-testid="privacy-policy-page">
        <div className="data-label">Legal</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Privacy Policy</h1>
        <p className="font-mono-data text-xs text-zinc-500 uppercase tracking-widest mt-2">
          Effective Date: {EFFECTIVE_DATE} · Last Updated: {LAST_UPDATED}
        </p>

        <div className="mt-6 text-zinc-700 leading-relaxed text-sm space-y-3">
          <p>
            Lien Scout App LLC (&ldquo;Lien Scout,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
            &ldquo;our&rdquo;) respects your privacy and is committed to protecting the information
            you provide when you use the Lien Scout website, mobile application, and related
            services (collectively, the &ldquo;Services&rdquo;).
          </p>
          <p>
            This Privacy Policy explains what information we collect, how we use it, when we may
            disclose it, and the choices available to you.
          </p>
          <p>
            By accessing or using Lien Scout, you acknowledge the practices described in this
            Privacy Policy.
          </p>
        </div>

        <Section title="1. Information We Collect">
          <h3 className="font-display font-semibold text-base mt-2">Information You Provide to Us</h3>
          <p className="mt-2">
            We may collect information that you voluntarily provide when you create an account,
            purchase or subscribe to our Services, contact us, request support, or otherwise
            interact with Lien Scout.
          </p>
          <p className="mt-2">This information may include:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Name</li>
            <li>Email address</li>
            <li>Username and account information</li>
            <li>Billing or subscription information</li>
            <li>Communications you send to us</li>
            <li>Saved searches, preferences, favorites, watchlists, or other information associated with your account</li>
            <li>Information you voluntarily provide when using features of the Services</li>
          </ul>
          <h3 className="font-display font-semibold text-base mt-4">Payment Information</h3>
          <p className="mt-2">
            If you purchase a subscription or other paid service, payments may be processed by a
            third-party payment processor, app store, or payment platform.
          </p>
          <p className="mt-2">
            Lien Scout may receive information relating to the transaction, such as subscription
            status, transaction identifiers, payment status, and limited billing information. We
            generally do not directly store complete credit or debit card numbers when payments are
            processed through third-party payment providers.
          </p>
          <h3 className="font-display font-semibold text-base mt-4">Information Collected Automatically</h3>
          <p className="mt-2">
            When you access or use our Services, certain technical information may be collected
            automatically, including:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>IP address</li>
            <li>Device type</li>
            <li>Operating system</li>
            <li>Browser type</li>
            <li>Device or application identifiers</li>
            <li>Date and time of access</li>
            <li>Pages, screens, or features accessed</li>
            <li>Referring website or source</li>
            <li>Application performance information</li>
            <li>Crash and diagnostic information</li>
            <li>General usage and interaction data</li>
          </ul>
          <p className="mt-2">
            We may use cookies, local storage, software development kits, analytics technologies,
            or similar technologies to collect some of this information.
          </p>
        </Section>

        <Section title="2. Property and Public Record Information">
          <p>
            Lien Scout provides tools designed to help users locate, organize, evaluate, and
            research real-estate-related information, including information concerning
            tax-delinquent properties, liens, property ownership, public records, auctions, and
            other property-related data.
          </p>
          <p className="mt-2">
            Much of the property information displayed through Lien Scout may originate from
            publicly available sources, including:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>County and municipal government records</li>
            <li>Tax assessor and tax collector records</li>
            <li>Court or governmental records</li>
            <li>Public property databases</li>
            <li>Public auction or tax-sale notices</li>
            <li>Government websites</li>
            <li>Other legally accessible public sources</li>
          </ul>
          <p className="mt-2">
            Information contained in public records may include property addresses, parcel numbers,
            ownership information, assessed values, tax information, lien information, sale
            information, and other information associated with real property.
          </p>
          <p className="mt-2">
            Lien Scout&rsquo;s collection, organization, processing, or display of publicly
            available information does not necessarily mean that the individuals identified in
            those public records are users of Lien Scout.
          </p>
        </Section>

        <Section title="3. How We Use Information">
          <p>We may use information we collect to:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Create and maintain user accounts</li>
            <li>Provide access to Lien Scout&rsquo;s features and databases</li>
            <li>Process subscriptions and transactions</li>
            <li>Authenticate users</li>
            <li>Save user preferences and searches</li>
            <li>Provide property-search and research tools</li>
            <li>Rank, organize, analyze, or enhance property information</li>
            <li>Improve the accuracy, functionality, and usability of the Services</li>
            <li>Develop new features</li>
            <li>Monitor application performance</li>
            <li>Diagnose technical problems</li>
            <li>Prevent fraud, abuse, unauthorized access, or security threats</li>
            <li>Communicate with users regarding their accounts or subscriptions</li>
            <li>Respond to customer-support requests</li>
            <li>Send service-related notices</li>
            <li>Send marketing communications when permitted by law</li>
            <li>Enforce our Terms of Service and other agreements</li>
            <li>Comply with applicable laws and legal obligations</li>
          </ul>
        </Section>

        <Section title="4. Artificial Intelligence and Automated Analysis">
          <p>
            Lien Scout may use automated systems, algorithms, or artificial intelligence
            technologies to organize, analyze, summarize, rank, classify, or otherwise process
            property-related data.
          </p>
          <p className="mt-2">
            These technologies may help users identify potentially relevant properties or reduce
            the amount of manual research required.
          </p>
          <p className="mt-2">
            Automated results are provided for informational and research purposes and should not
            be considered legal, financial, investment, title, appraisal, or professional advice.
          </p>
          <p className="mt-2">
            Users should independently verify important property information with the applicable
            government agency, county office, title professional, attorney, or other authoritative
            source before making financial or legal decisions.
          </p>
        </Section>

        <Section title="5. How We Share Information">
          <p>
            We do not sell personal information simply for the purpose of providing third parties
            with lists of our users.
          </p>
          <p className="mt-2">We may disclose information in the following circumstances:</p>
          <h3 className="font-display font-semibold text-base mt-4">Service Providers</h3>
          <p className="mt-2">
            We may share information with companies that perform services on our behalf, including
            providers of:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Cloud hosting</li>
            <li>Database infrastructure</li>
            <li>Website hosting</li>
            <li>Application infrastructure</li>
            <li>Analytics</li>
            <li>Customer support</li>
            <li>Email communications</li>
            <li>Security and fraud prevention</li>
            <li>Payment processing</li>
            <li>Subscription management</li>
            <li>Software development and maintenance</li>
          </ul>
          <p className="mt-2">
            These providers may access information only as necessary to provide services to Lien
            Scout or as otherwise permitted by their agreements and applicable law.
          </p>
          <h3 className="font-display font-semibold text-base mt-4">Legal Requirements</h3>
          <p className="mt-2">
            We may disclose information when we reasonably believe disclosure is necessary to:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Comply with applicable law, regulation, subpoena, court order, or governmental request</li>
            <li>Protect the rights or property of Lien Scout</li>
            <li>Investigate fraud, security incidents, or misuse of the Services</li>
            <li>Protect the safety of users or others</li>
            <li>Establish, exercise, or defend legal claims</li>
          </ul>
          <h3 className="font-display font-semibold text-base mt-4">Business Transactions</h3>
          <p className="mt-2">
            If Lien Scout is involved in a merger, acquisition, financing, restructuring,
            bankruptcy, asset sale, or similar transaction, information associated with the
            Services may be transferred as part of that transaction, subject to applicable law.
          </p>
          <h3 className="font-display font-semibold text-base mt-4">With Your Direction or Consent</h3>
          <p className="mt-2">
            We may disclose information when you direct us to do so or otherwise provide your
            consent.
          </p>
        </Section>

        <Section title="6. Public Information Is Not Private Account Information">
          <p>
            Property ownership information and other information obtained from government or
            public-record sources should be distinguished from personal information collected
            directly from Lien Scout users.
          </p>
          <p className="mt-2">
            For example, a property owner&rsquo;s name appearing in a county tax record may be
            displayed through Lien Scout because it appears in a public governmental record. This
            does not mean that the property owner created a Lien Scout account or provided that
            information directly to us.
          </p>
        </Section>

        <Section title="7. Cookies and Similar Technologies">
          <p>Our website or Services may use cookies and similar technologies to:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Maintain user sessions</li>
            <li>Remember preferences</li>
            <li>Authenticate accounts</li>
            <li>Understand how the Services are used</li>
            <li>Measure performance</li>
            <li>Detect security threats</li>
            <li>Improve functionality</li>
          </ul>
          <p className="mt-2">
            You may be able to limit cookies through your browser or device settings. Disabling
            certain technologies may affect the functionality of the Services.
          </p>
        </Section>

        <Section title="8. Analytics">
          <p>We may use analytics providers to understand how users interact with Lien Scout.</p>
          <p className="mt-2">
            Analytics information may include device information, approximate location derived from
            an IP address, pages or features accessed, session duration, application performance,
            and similar usage information.
          </p>
          <p className="mt-2">
            We use this information to understand user behavior, improve the Services, troubleshoot
            problems, and develop new features.
          </p>
        </Section>

        <Section title="9. Advertising">
          <p>Lien Scout may advertise its Services through third-party advertising platforms.</p>
          <p className="mt-2">
            Those platforms may collect or process information in accordance with their own privacy
            policies.
          </p>
          <p className="mt-2">
            If Lien Scout introduces advertising inside the application or uses information for
            targeted or cross-context behavioral advertising, this Privacy Policy will be updated
            as appropriate and any legally required choices or permissions will be provided.
          </p>
        </Section>

        <Section title="10. Data Security">
          <p>
            We use reasonable administrative, technical, and organizational safeguards designed to
            protect information against unauthorized access, alteration, disclosure, destruction,
            or misuse.
          </p>
          <p className="mt-2">
            However, no internet transmission, database, or electronic storage system can be
            guaranteed to be completely secure. Accordingly, we cannot guarantee absolute security
            of information.
          </p>
          <p className="mt-2">
            Users are responsible for maintaining the confidentiality of their login credentials
            and should notify us if they believe their account has been accessed without
            authorization.
          </p>
        </Section>

        <Section title="11. Data Retention">
          <p>We may retain personal information for as long as reasonably necessary to:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Provide the Services</li>
            <li>Maintain your account</li>
            <li>Complete transactions</li>
            <li>Maintain business and financial records</li>
            <li>Prevent fraud or abuse</li>
            <li>Resolve disputes</li>
            <li>Enforce agreements</li>
            <li>Satisfy legal, tax, accounting, or regulatory obligations</li>
          </ul>
          <p className="mt-2">
            When information is no longer reasonably necessary, we may delete, anonymize, or
            aggregate it in accordance with applicable law.
          </p>
          <p className="mt-2">
            Public-record information may remain available from its original governmental or public
            source even if it is removed from Lien Scout.
          </p>
        </Section>

        <Section title="12. Account Deletion and Privacy Requests">
          <p>
            You may request deletion of your Lien Scout account and associated personal
            information, subject to information we are permitted or required to retain by law.
          </p>
          <p className="mt-2">
            Depending on your jurisdiction, you may also have rights concerning your personal
            information, including rights to:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Request access to certain personal information</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of certain information</li>
            <li>Obtain information about how your data is processed</li>
            <li>Withdraw certain consent</li>
            <li>Opt out of certain communications</li>
            <li>Exercise other privacy rights provided by applicable law</li>
          </ul>
          <p className="mt-2">
            To submit a privacy request, please contact Lien Scout using the contact information
            provided below.
          </p>
          <p className="mt-2">We may need to verify your identity before processing certain requests.</p>
        </Section>

        <Section title="13. Public Record Correction Requests">
          <p>
            Lien Scout attempts to provide useful and accurate property information, but
            information obtained from government agencies and other public sources may be delayed,
            incomplete, inaccurate, or outdated.
          </p>
          <p className="mt-2">
            If you believe property information displayed through Lien Scout is incorrect, you may
            contact us and identify the applicable property and information.
          </p>
          <p className="mt-2">
            Where appropriate, we may correct information within our system. However, if the
            information originates from an official government record, you may also need to contact
            the government agency responsible for maintaining the underlying record.
          </p>
        </Section>

        <Section title="14. Email and Marketing Communications">
          <p>
            We may send transactional communications concerning your account, subscription,
            security, billing, or changes to the Services.
          </p>
          <p className="mt-2">
            If you receive promotional or marketing emails from us, you may opt out using the
            unsubscribe instructions included in those communications.
          </p>
          <p className="mt-2">
            Opting out of promotional communications does not prevent us from sending important
            account, billing, security, or service-related messages.
          </p>
        </Section>

        <Section title="15. Children's Privacy">
          <p>
            Lien Scout is intended for adults and is not directed toward children under the age of
            13.
          </p>
          <p className="mt-2">
            We do not knowingly collect personal information from children under 13 through the
            Services. If we learn that we have collected personal information from a child under 13
            without appropriate authorization, we will take reasonable steps to delete that
            information.
          </p>
        </Section>

        <Section title="16. Third-Party Websites and Services">
          <p>
            Lien Scout may contain links to county websites, government agencies, mapping services,
            payment providers, or other third-party websites and services.
          </p>
          <p className="mt-2">
            We do not control the privacy practices of third parties. Your interaction with a
            third-party website or service is governed by that third party&rsquo;s privacy policy
            and terms.
          </p>
          <p className="mt-2">
            We encourage users to review the privacy practices of third-party services they access.
          </p>
        </Section>

        <Section title="17. Do Not Track and Privacy Controls">
          <p>Some browsers and devices provide privacy controls or &ldquo;Do Not Track&rdquo; signals.</p>
          <p className="mt-2">
            Because standards regarding these signals continue to vary, our Services may not
            respond to every browser-based Do Not Track signal.
          </p>
          <p className="mt-2">
            Where applicable law requires recognition of a legally valid opt-out preference signal,
            we will process such signals as required by applicable law.
          </p>
        </Section>

        <Section title="18. U.S. State Privacy Rights">
          <p>
            Residents of certain U.S. states may have additional privacy rights under applicable
            state law.
          </p>
          <p className="mt-2">
            Depending on where you reside and whether the applicable law applies to Lien Scout,
            these rights may include the ability to request access, correction, deletion,
            portability, or information regarding certain uses or disclosures of personal
            information.
          </p>
          <p className="mt-2">
            You may also have the right to appeal certain decisions regarding a privacy request.
          </p>
          <p className="mt-2">
            Lien Scout will not discriminate against you for exercising privacy rights provided by
            applicable law.
          </p>
        </Section>

        <Section title="19. International Users">
          <p>
            Lien Scout is primarily designed to provide information regarding property located
            within the United States.
          </p>
          <p className="mt-2">
            If you access the Services from outside the United States, you understand that
            information may be processed or stored in the United States or other jurisdictions in
            which our service providers operate, subject to applicable law.
          </p>
        </Section>

        <Section title="20. Changes to This Privacy Policy">
          <p>
            We may update this Privacy Policy periodically to reflect changes in our Services,
            technology, business practices, or legal obligations.
          </p>
          <p className="mt-2">
            When we make changes, we will update the &ldquo;Last Updated&rdquo; date at the
            beginning of this Privacy Policy.
          </p>
          <p className="mt-2">
            If a change materially affects how we use personal information, we may provide
            additional notice when required by law.
          </p>
          <p className="mt-2">
            Your continued use of Lien Scout after an updated Privacy Policy becomes effective
            constitutes acknowledgment of the revised policy, to the extent permitted by law.
          </p>
        </Section>

        <Section title="21. Contact Us">
          <p>
            If you have questions about this Privacy Policy, want to exercise a privacy right, or
            wish to submit a privacy-related request, contact:
          </p>
          <p className="mt-3">
            <strong>Lien Scout App LLC</strong>
            <br />
            Website: <a href="https://lienscoutapp.com" className="text-red-600 hover:underline">lienscoutapp.com</a>
            <br />
            Email: <a href="mailto:sbrown@lienscoutapp.com" className="text-red-600 hover:underline">sbrown@lienscoutapp.com</a>
          </p>
          <p className="mt-2">
            For privacy requests, please include sufficient information for us to understand and
            respond to your request.
          </p>
        </Section>

        <p className="mt-10 font-mono-data text-xs text-zinc-500">
          © 2026 Lien Scout App LLC. All rights reserved.
        </p>
      </section>
    </MarketingShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
      <div className="mt-2 text-zinc-700 leading-relaxed text-sm">{children}</div>
    </div>
  );
}
